'use client';

import React, { createContext, ReactNode, useContext, useState } from 'react';
import DemoModal from '@/components/blocks/DemoModal';
import widgetConfig, { WidgetConfigPages } from '@/utils/widgetConfigs';
import { useTranslation } from '@/hooks/useTranslation';
import { HiOutlineChartBar } from 'react-icons/hi2';
import { BsCpu, BsEmojiHeartEyes } from 'react-icons/bs';
import { PiTreeStructure } from 'react-icons/pi';
import { BiDollar } from 'react-icons/bi';
import { FaRobot, FaVideo } from 'react-icons/fa';
import { MdCall } from 'react-icons/md';

type ModalType =
  | 'ROI_CALCULATOR'
  | 'DISCOVER_CHATBOT_TYPE'
  | 'DISCOVER_HUMAN_CLONE_LINK'
  | 'REVENUE_CALCULATOR'
  | 'DISCOVER_CHATBOT_TEXT'
  | 'DISCOVER_CHATBOT_AI'
  | 'DISCOVER_CALLBOT'
  | 'DISCOVER_VIDEOBOT';

interface ModalContextType {
  openModal: (type: ModalType, props?: any) => void;
  closeModal: () => void;
  modalType: ModalType | null;
  modalProps: any;
}

const ModalContext = createContext<ModalContextType>({
  openModal: () => {},
  closeModal: () => {},
  modalType: null,
  modalProps: {},
});

export const ModalProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [modalProps, setModalProps] = useState({});

  const openModal = (type: ModalType, props: any = {}) => {
    setModalType(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setModalType(null);
    setModalProps({});
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, modalType, modalProps }}>
      {children}
      <ModalRenderer />
    </ModalContext.Provider>
  );
};

// Composant de rendu conditionnel des modals
const ModalRenderer: React.FC = ({}) => {
  const { modalType, modalProps, closeModal } = useContext(ModalContext);
  const { t, locale } = useTranslation();

  const renderModal = () => {
    switch (modalType) {
      case 'ROI_CALCULATOR':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.ROICalculator, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('ROICalculator/title')}
            infoText={t('ROICalculator/infoText')}
            loadingTest={t('ROICalculator/loadingText')}
            demoComponent={'ModalAutoPlayAllOptionActive'}
            icon={<HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'DISCOVER_CHATBOT_TYPE':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverChatbotType, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('DiscoverChatbotType/title')}
            infoText={t('DiscoverChatbotType/infoText')}
            loadingTest={t('DiscoverChatbotType/loadingText')}
            demoComponent={'ModalAutoPlayAllOptionActive'}
            icon={<PiTreeStructure className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'DISCOVER_HUMAN_CLONE_LINK':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverHumanCloneLink, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('DiscoverHumanCloneLink/title')}
            infoText={t('DiscoverHumanCloneLink/infoText')}
            loadingTest={t('DiscoverHumanCloneLink/loadingText')}
            demoComponent={'ModalAutoPlayAllOptionActive'}
            icon={<BsEmojiHeartEyes className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'REVENUE_CALCULATOR':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.RevenueCalculator, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('RevenueCalculator/title')}
            infoText={t('RevenueCalculator/infoText')}
            loadingTest={t('RevenueCalculator/loadingText')}
            demoComponent={'ModalAutoPlayAllOptionActive'}
            icon={<BiDollar className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'DISCOVER_CHATBOT_TEXT':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverChatbotText, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('DiscoverChatbotText/title')}
            infoText={t('DiscoverChatbotText/infoText')}
            loadingTest={t('DiscoverChatbotText/loadingText')}
            demoComponent={'ModalAutoPlayAllOptionActive'}
            icon={<FaRobot className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'DISCOVER_CHATBOT_AI':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverChatbotAI, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('DiscoverChatbotAI/title')}
            infoText={t('DiscoverChatbotAI/infoText')}
            loadingTest={t('DiscoverChatbotAI/loadingText')}
            demoComponent={'ModalAutoPlayAllOptionActive'}
            icon={<BsCpu className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'DISCOVER_VIDEOBOT':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverVideobot, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('DiscoverVideobot/title')}
            infoText={t('DiscoverVideobot/infoText')}
            loadingTest={t('DiscoverVideobot/loadingText')}
            demoComponent={'ModalWaitingPressPlay'}
            icon={<FaVideo className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      case 'DISCOVER_CALLBOT':
        return (
          <DemoModal
            botId={widgetConfig.getWidgetCode(WidgetConfigPages.DiscoverCallBot, locale)}
            isOpen={true}
            onClose={function (): void {
              closeModal();
            }}
            title={t('DiscoverCallbot/title')}
            infoText={t('DiscoverCallbot/infoText')}
            loadingTest={t('DiscoverCallbot/loadingText')}
            demoComponent={'ModalForCallBot'}
            icon={<MdCall className="tw-w-6 tw-h-6 tw-text-blue-600" />}
          />
        );
      default:
        return null;
    }
  };

  return renderModal();
};

export const useModal = () => useContext(ModalContext);
